import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { LocalNotifications } from '@capacitor/local-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform() as 'ios' | 'android' | 'web';

/**
 * Platform whose selling rules apply (live config `storeBilling`). Native = the real platform. Dev / demo web builds
 * can preview another one with `?asPlatform=android` (remembered per tab; `?asPlatform=web` resets) so e2e suites and
 * store screenshots see the Android experience in a browser.
 */
export const billingPlatform: 'ios' | 'android' | 'web' = (() => {
  if (isNative) return platform;
  if (!(import.meta.env.DEV || import.meta.env.VITE_DEMO_TRIAL === '1') || typeof window === 'undefined') return 'web';
  try {
    const q = new URLSearchParams(window.location.search).get('asPlatform');
    if (q === 'ios' || q === 'android' || q === 'web') sessionStorage.setItem('sk-as-platform', q);
    const v = sessionStorage.getItem('sk-as-platform');
    return v === 'ios' || v === 'android' ? v : 'web';
  } catch {
    return 'web';
  }
})();

export async function initNativeChrome(): Promise<void> {
  if (typeof document !== 'undefined') document.documentElement.dataset.native = String(isNative);
  if (!isNative) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.hide();
    // Landscape-only on devices (the web build adapts to portrait instead).
    const orientation = (screen as unknown as { orientation?: { lock?: (o: string) => Promise<void> } }).orientation;
    await orientation?.lock?.('landscape').catch(() => undefined);
  } catch {
    /* plugin unavailable */
  }
}

export function haptic(kind: 'light' | 'success' | 'error' = 'light'): void {
  if (!isNative) return;
  if (kind === 'light') void Haptics.impact({ style: ImpactStyle.Light });
  else void Haptics.notification({ type: kind === 'success' ? NotificationType.Success : NotificationType.Error });
}

const REMINDER_BASE = 4200;

/** Ask for notification permission (web: Notification API; native: local notifications). */
export async function requestNotificationPermission(): Promise<boolean> {
  try {
    if (!isNative) {
      if (!('Notification' in window)) return false;
      if (Notification.permission === 'default') await Notification.requestPermission();
      return Notification.permission === 'granted';
    }
    const perm = await LocalNotifications.requestPermissions();
    return perm.display === 'granted';
  } catch {
    return false;
  }
}

/**
 * Weekly drawing reminders on the chosen days (0 = Sunday) at HH:MM. Native schedules local
 * notifications; the web only records the preference.
 */
export async function scheduleReminders(days: number[], time: string, title: string, body: string): Promise<boolean> {
  if (!isNative) return requestNotificationPermission();
  if (!(await requestNotificationPermission())) return false;
  const [hour, minute] = time.split(':').map(Number);
  await LocalNotifications.cancel({ notifications: [0, 1, 2, 3, 4, 5, 6].map((d) => ({ id: REMINDER_BASE + d })) });
  await LocalNotifications.schedule({
    notifications: days.map((d) => ({
      id: REMINDER_BASE + d,
      title,
      body,
      schedule: { on: { weekday: d + 1, hour: hour!, minute: minute! }, repeats: true, allowWhileIdle: true },
    })),
  });
  return true;
}

export async function cancelReminders(): Promise<void> {
  if (isNative) await LocalNotifications.cancel({ notifications: [0, 1, 2, 3, 4, 5, 6].map((d) => ({ id: REMINDER_BASE + d })) });
}
